import React, { useEffect, useState, FC } from 'react';
import { documentService } from '../../../../utils/window.utils';
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';
import ctaTypes from '../../../../enums/CTA.enums';
import Accordion, { TAccordionProps } from '../../../@shared/filters/components/accordion';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import { NewsListingModel } from '../../models/newsListing.model';
import { TNewsListingFilter, TSelectedNewsFilters } from '../../types/newsListing.types';
import { QueryParamsConst } from '../../../../constants/query-params.constants';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import './styles.scss';

const MobileFilter: FC<{ model: NewsListingModel; selectedFilters: TSelectedNewsFilters }> = ({ model, selectedFilters }) => {
  const [shouldOpenFilterModal, setShouldOpenFilterModal] = useState(false);
  const [selectedFiltersCopy, setSelectedFiltersCopy] = useState(selectedFilters);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const docElement = documentService();
    if (!docElement?.body) return;

    if (shouldOpenFilterModal) docElement.body.classList.add('body--with-filter-model');
    else docElement.body.classList.remove('body--with-filter-model');
  }, [shouldOpenFilterModal]);

  const { YEAR, TYPE, LINE, INDUSTRY } = QueryParamsConst;
  // retaining as it might be used at a later time
  // const shouldShowFilterWrapper =
  //   !(
  //     selectedFiltersCopy[YEAR] === selectedFilters[YEAR] &&
  //     selectedFiltersCopy[LINE] == selectedFilters[LINE] &&
  //     selectedFiltersCopy[TYPE] === selectedFilters[TYPE] &&
  //     selectedFiltersCopy[INDUSTRY] === selectedFilters[INDUSTRY]
  //   ) ||
  //   selectedFiltersCopy[YEAR] !== '' ||
  //   selectedFiltersCopy[LINE] !== '' ||
  //   selectedFiltersCopy[TYPE] !== '' ||
  //   selectedFiltersCopy[INDUSTRY] !== '';

  const getAccordionOptionsByInd = (filter?: TNewsListingFilter, titleDictionaryKey?: string): TAccordionProps['accordionOptions'] => {
    if (!filter) return [];
    return [
      { id: filter.value, title: t(titleDictionaryKey || '') },
      ...(filter?.tags?.map((tag) => ({ id: tag.id?.toString(), title: tag.value })) || []),
    ];
  };

  const accordionsProps = [
    {
      filter: model.findFilterByName('year'),
      queryKey: YEAR,
      id: 'year-accordion',
      titleDictionaryKey: 'cnhi-news-all-years',
      accordionTitle: <DictionaryTerm keyword="cnhi-news-year-filter-title" />,
    },
    {
      filter: model.findFilterByName('line'),
      queryKey: LINE,
      id: 'line-accordion',
      titleDictionaryKey: 'cnhi-news-all-lines',
      accordionTitle: <DictionaryTerm keyword="cnhi-news-line-filter-title" />,
    },
    {
      filter: model.findFilterByName('type'),
      queryKey: TYPE,
      id: 'type-accordion',
      titleDictionaryKey: 'cnhi-news-all-types',
      accordionTitle: <DictionaryTerm keyword="cnhi-news-type-filter-title" />,
    },
    {
      filter: model.findFilterByName('industry'),
      queryKey: INDUSTRY,
      id: 'industry-accordion',
      titleDictionaryKey: 'cnhi-news-all-industries',
      accordionTitle: <DictionaryTerm keyword="cnhi-news-industry-filter-title" />,
    },
  ];

  const revertToOldFilters = () => model.updateQueryByBulkKeys(navigate, selectedFiltersCopy);
  const toggleModal = () => setShouldOpenFilterModal(!shouldOpenFilterModal);

  return (
    <div className="news-listing-with-filter--mbl">
      <div
        className="news-filter-modal-switch"
        onClick={() => {
          toggleModal();
          revertToOldFilters();
        }}
      >
        <Icon iconName="set-nav-services-funnel" color="primary" size={iconSize['mbl']} />
        <DictionaryTerm keyword="cnhi-news-filter-title" />
      </div>
      {shouldOpenFilterModal && (
        <div className="news-filter-modal">
          <div className="news-filter-modal__header">
            <span className="news-filter-modal__title">
              <DictionaryTerm keyword="cnhi-news-filter-title" />
            </span>
            <Icon
              iconName="action-close"
              color="white"
              size={iconSize['dsk']}
              onClick={() => {
                setShouldOpenFilterModal(false);
                revertToOldFilters();
              }}
            />
          </div>
          <div className="news-filter-modal__body">
            {accordionsProps.map(({ filter, id, accordionTitle, queryKey, titleDictionaryKey }) => {
              const options = getAccordionOptionsByInd(filter, titleDictionaryKey);
              if (!options?.length || filter.hidden) return null;
              return (
                <Accordion
                  key={id}
                  accordionTitle={accordionTitle}
                  accordionOptions={options}
                  handleOptionClick={(ind) => model.updateQueryByKey(navigate, queryKey, ind === 0 ? '' : (options?.[ind].id as string))}
                  selectedOptionInd={(filter?.tags?.findIndex((_) => _.id?.toString() === selectedFilters[queryKey]) ?? -1) + 1}
                />
              );
            })}
          </div>
          {
            // retaining as it might be used at a later time
            // shouldShowFilterWrapper && (
            <div className="news-filter-modal__ctas">
              <span
                className={`button ${ctaTypes['primaryBlack']}`}
                onClick={() => {
                  model.resetFilter(navigate);
                  setSelectedFiltersCopy({
                    [QueryParamsConst.PAGE]: '',
                    [QueryParamsConst.YEAR]: '',
                    [QueryParamsConst.LINE]: '',
                    [QueryParamsConst.TYPE]: '',
                    [QueryParamsConst.INDUSTRY]: '',
                  });
                }}
              >
                <DictionaryTerm keyword="cnhi-news-reset-filters" />
              </span>
              <span
                className={`button ${ctaTypes['primaryOrange']}`}
                onClick={() => {
                  setSelectedFiltersCopy(selectedFilters);
                  toggleModal();
                }}
              >
                <DictionaryTerm keyword="cnhi-news-apply-filters" />
              </span>
            </div>
          }
        </div>
      )}
    </div>
  );
};

export default MobileFilter;
